import { useEffect, useRef, useState } from "react";
import { Autocomplete, TextField, CircularProgress, AutocompleteProps } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchTiers, clearTiersData } from "../../reduxStore/TiersSlice";
import { RootState, AppDispatch } from '../../reduxStore/Store';
import { fromEvent } from "rxjs";
import { debounceTime, switchMap } from "rxjs/operators";
import { Tiers } from "../../models/models";
import { TypeTiers } from "../../constants/enums";
import { labels } from "../../constants/labels";

interface EmployeAutoCProps
extends Partial<AutocompleteProps<Tiers, false, false, false>>  {
  onEmployeChange: (employe: Tiers | null) => void;
  selectedEmploye?: Tiers | null;
  required?: boolean;
}

const type = TypeTiers.EMPLOYE; // Hardcoded type
const limit = 10; // Hardcoded limit
const offset = 0; // Hardcoded offset

const EmployeAutoC: React.FC<EmployeAutoCProps> = ({ onEmployeChange, selectedEmploye, required, ...autocompleteProps }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { tiersData, loading } = useSelector((state: RootState) => state.TiersSlice);
  const [employe, setEmploye] = useState<Tiers | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setEmploye(selectedEmploye?.id ? selectedEmploye : null);
  }, [selectedEmploye]);

  useEffect(() => {
    if (inputRef.current) {
      const subscription = fromEvent(inputRef.current, "input")
        .pipe(
          debounceTime(500),
          switchMap((event: any) => {
            const nom = event.target.value;
            if (nom.length > 2) {
              dispatch(fetchTiers({ type, nom, limit, offset }));
            } else {
              dispatch(clearTiersData());
            }
            return [];
          })
        )
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [dispatch]);

  const handleChange = (_: any, newValue: Tiers | null) => {
    setEmploye(newValue);
    onEmployeChange(newValue);
  };

  return (
    <Autocomplete
      options={tiersData}
      value={employe}
      getOptionLabel={(option: Tiers) => `${option.nom} ${option.prenom}`}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      loading={loading}
      onChange={handleChange}
      onBlur={() => dispatch(clearTiersData())}
      {...autocompleteProps}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          label={labels.employe}
          variant="outlined"
          required={!!required}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          size="small"
        />
      )}
    />
  );
};

export default EmployeAutoC;
